import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { toast } from 'sonner';

import { useLanguage } from '@/contexts/language-context';
import { Button } from '@/components/ui/button';
import {
  claimInvoicePaid,
  getSharedInvoice,
  sharedInvoiceQueryKey,
  startInvoiceStripeCheckout,
  type PublicPaymentMethodView,
  type SharedInvoiceDto,
} from '@/lib/api/public-invoices';
import { paymentMethodLabel } from '@/routes/jobs/helpers';
import type { PaymentMethod } from '@/lib/api/jobs';

import { SharedInvoiceView } from './shared-invoice-view';

function methodDetail(method: PublicPaymentMethodView): string | null {
  const config = method.config ?? {};
  const value = config.phone ?? config.handle ?? config.username ?? config.email ?? null;
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

// Venmo's web deep link — on mobile with the app installed, iOS/Android hand
// this https:// URL off to the app with the payer, amount, and note
// pre-filled; otherwise it just opens the Venmo site. The `venmo://` custom
// scheme has no registered handler outside the app hand-off, so browsers
// (desktop especially) refuse to launch it — this form always has a handler.
function venmoDeepLink(handle: string, amount: number, note: string): string {
  const username = handle.replace(/^@/, '');
  const params = new URLSearchParams({
    txn: 'pay',
    amount: amount.toFixed(2),
    note,
  });
  return `https://venmo.com/${encodeURIComponent(username)}?${params.toString()}`;
}

// Venmo has one free-text note field — no separate title — so it carries both.
function venmoNote(invoice: SharedInvoiceDto): string {
  return invoice.job_name ? `${invoice.company_name} - ${invoice.job_name}` : invoice.company_name;
}

// Public, unauthenticated page for `/i/:token`. Renders outside the auth
// guard and app shell — no nav, no login redirect, no authed fetches. The
// token is capability-bearing, so it is never logged or sent anywhere but
// the API. Mirrors `routes/public-quote/index.tsx`.
export function PublicInvoice() {
  const { t } = useLanguage();
  const { token } = useParams<{ token: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: sharedInvoiceQueryKey(token ?? ''),
    queryFn: () => getSharedInvoice(token!),
    enabled: Boolean(token),
    retry: false,
  });
  const { refetch } = query;

  // Guard the manual retry so a frustrated tap can't hammer the rate limiter.
  const [cooling, setCooling] = useState(false);
  const coolTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retry = useCallback(() => {
    setCooling(true);
    if (coolTimerRef.current) clearTimeout(coolTimerRef.current);
    coolTimerRef.current = setTimeout(() => setCooling(false), 3000);
    void refetch();
  }, [refetch]);

  // Stripe redirects back here after Checkout — the webhook that flips the
  // invoice to `paid` lands asynchronously, so this just nudges a refetch and
  // clears the marker rather than trusting the redirect itself.
  useEffect(() => {
    const payment = searchParams.get('payment');
    if (!payment) return;
    if (payment === 'success') {
      toast.success(t.sharedInvoicePaidMsg);
      void refetch();
    } else if (payment === 'cancelled') {
      toast.error(t.sharedInvoiceCheckoutError);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('payment');
    setSearchParams(next, { replace: true });
    // Only react to the redirect marker once, on arrival.
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, []);

  const [claimedByName, setClaimedByName] = useState('');
  const [selectedMethodId, setSelectedMethodId] = useState<string | null>(null);

  const claimMutation = useMutation({
    mutationFn: (paymentMethodId: string) =>
      claimInvoicePaid(token!, {
        payment_method_id: paymentMethodId,
        claimed_by_name: claimedByName.trim() || undefined,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(sharedInvoiceQueryKey(token ?? ''), data);
      toast.success(t.sharedInvoicePaidMsg);
    },
    onError: (err) => {
      if (isAxiosError(err) && err.response?.status === 409) {
        toast.error(t.sharedInvoiceAlreadyClaimed);
        void queryClient.invalidateQueries({ queryKey: sharedInvoiceQueryKey(token ?? '') });
        return;
      }
      toast.error(t.sharedInvoiceClaimError);
    },
  });

  const checkoutMutation = useMutation({
    mutationFn: () => startInvoiceStripeCheckout(token!),
    onSuccess: (data) => {
      window.location.href = data.checkout_url;
    },
    onError: () => {
      toast.error(t.sharedInvoiceCheckoutError);
    },
  });

  useEffect(() => {
    return () => {
      if (coolTimerRef.current) clearTimeout(coolTimerRef.current);
    };
  }, []);

  const errorStatus = isAxiosError(query.error) ? query.error.response?.status : undefined;

  let content: React.ReactNode;
  if (query.isPending) {
    content = <InvoiceSkeleton />;
  } else if (query.isSuccess) {
    const invoice = query.data;
    const nonStripeMethods = invoice.payment_methods.filter((m) => m.type !== 'stripe');
    const stripeMethod = invoice.payment_methods.find((m) => m.type === 'stripe');

    content = (
      <div className="flex flex-col gap-4">
        <SharedInvoiceView invoice={invoice} />

        {invoice.status === 'unpaid' && (
          <div
            className="flex flex-col gap-4 rounded-lg border bg-background p-5"
            data-testid="shared-invoice-actions"
          >
            {nonStripeMethods.length > 0 && (
              <div className="flex flex-col gap-2">
                <span className="text-sm font-medium">{t.sharedInvoiceSelectMethod}</span>
                {nonStripeMethods.map((method) => (
                  <div key={method.id}>
                    <label
                      className="flex items-center gap-2 text-sm"
                      data-testid={`shared-invoice-method-${method.id}`}
                    >
                      <input
                        type="radio"
                        name="payment-method"
                        checked={selectedMethodId === method.id}
                        onChange={() => setSelectedMethodId(method.id)}
                      />
                      <span>
                        {paymentMethodLabel(method.type as PaymentMethod, t)}
                        {methodDetail(method) && (
                          <span className="text-muted-foreground"> — {methodDetail(method)}</span>
                        )}
                      </span>
                    </label>
                    {method.type === 'venmo' && methodDetail(method) && (
                      <a
                        href={venmoDeepLink(
                          methodDetail(method)!,
                          invoice.total_amount,
                          venmoNote(invoice),
                        )}
                        onClick={() => setSelectedMethodId(method.id)}
                        className="ml-6 text-xs text-primary underline"
                        data-testid={`shared-invoice-venmo-link-${method.id}`}
                      >
                        {t.sharedInvoiceOpenVenmo}
                      </a>
                    )}
                  </div>
                ))}

                <label className="flex flex-col gap-1.5 text-sm">
                  <span className="font-medium">{t.sharedInvoiceClaimedByName}</span>
                  <input
                    type="text"
                    value={claimedByName}
                    maxLength={200}
                    required
                    aria-required="true"
                    onChange={(e) => setClaimedByName(e.target.value)}
                    data-testid="shared-invoice-claimed-name"
                    className="w-full px-3 py-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </label>

                <Button
                  type="button"
                  size="sm"
                  disabled={!selectedMethodId || !claimedByName.trim() || claimMutation.isPending}
                  onClick={() => selectedMethodId && claimMutation.mutate(selectedMethodId)}
                  data-testid="shared-invoice-claim-paid"
                >
                  {t.sharedInvoiceIvePaid}
                </Button>
              </div>
            )}

            {stripeMethod && (
              <div className="flex flex-col gap-2 border-t pt-4 first:border-t-0 first:pt-0">
                <Button
                  type="button"
                  size="sm"
                  variant={nonStripeMethods.length > 0 ? 'outline' : 'default'}
                  disabled={checkoutMutation.isPending}
                  onClick={() => checkoutMutation.mutate()}
                  data-testid="shared-invoice-pay-by-card"
                >
                  {t.sharedInvoicePayByCard}
                </Button>
              </div>
            )}
          </div>
        )}

        {invoice.status === 'pending_confirmation' && (
          <p
            className="text-sm text-center text-muted-foreground rounded-lg border bg-background p-5"
            data-testid="shared-invoice-pending"
          >
            {t.sharedInvoicePendingConfirmation}
          </p>
        )}

        {invoice.status === 'paid' && (
          <p
            className="text-sm text-center text-muted-foreground rounded-lg border bg-background p-5"
            data-testid="shared-invoice-paid"
          >
            {t.sharedInvoicePaid}
          </p>
        )}
      </div>
    );
  } else if (errorStatus === 404) {
    content = <Message text={t.sharedInvoiceInvalid} />;
  } else if (errorStatus === 429) {
    content = (
      <Message
        text={t.sharedInvoiceRateLimited}
        onRetry={retry}
        retryDisabled={cooling || query.isFetching}
      />
    );
  } else {
    content = (
      <Message text={t.sharedInvoiceError} onRetry={retry} retryDisabled={query.isFetching} />
    );
  }

  return (
    <div className="min-h-svh bg-muted/30 px-4 py-10 flex items-start justify-center">
      <div className="w-full max-w-lg">{content}</div>
    </div>
  );
}

function Message({
  text,
  onRetry,
  retryDisabled,
}: {
  text: string;
  onRetry?: () => void;
  retryDisabled?: boolean;
}) {
  const { t } = useLanguage();
  return (
    <div
      className="rounded-lg border bg-background p-8 flex flex-col items-center gap-4 text-center"
      data-testid="shared-invoice-message"
    >
      <p className="text-sm text-muted-foreground">{text}</p>
      {onRetry && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={retryDisabled}
          onClick={onRetry}
          data-testid="shared-invoice-retry"
        >
          {t.sharedInvoiceRetry}
        </Button>
      )}
    </div>
  );
}

function InvoiceSkeleton() {
  return (
    <div
      className="rounded-lg border bg-background p-5 flex flex-col gap-5 animate-pulse"
      data-testid="shared-invoice-skeleton"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex flex-col gap-2 w-1/2">
          <div className="h-5 rounded bg-muted" />
          <div className="h-4 rounded bg-muted w-3/4" />
        </div>
        <div className="h-5 w-16 rounded-full bg-muted" />
      </div>
      <div className="flex flex-col gap-2">
        <div className="h-4 rounded bg-muted" />
        <div className="h-4 rounded bg-muted" />
        <div className="h-4 rounded bg-muted w-2/3" />
      </div>
      <div className="h-6 rounded bg-muted w-1/3 self-end" />
    </div>
  );
}
